//
// Copyright (c) 2011-2021 by Curalate, Inc.
//

import {
  BatchCreatePostsResponse,
  BatchUpsertScheduledPostsResponse,
  ScheduledPostTimeUpdate,
} from '@bvengineering/universal-scheduling-gateway-service-types';

import {
  ConnectionId,
  TConnectionConfig,
} from '@apis/universal-posts/connections/universalPostConnectionModel';
import {
  ICreateUpdateScheduledPost,
  IGetBatchScheduledPostsResponse,
  IGetScheduledPostsResponse,
  IScheduledPost,
  IScheduledPostResponse,
  ISearchScheduledPostsRequest,
} from '@apis/universal-posts/scheduled-posts/universalScheduledPostsApiModel';
import { deserializeScheduledPost } from '@apis/universal-posts/scheduled-posts/universalScheduledPostsApiUtils';
import PostableId from '@models/PostableId';
import { makeParams } from '@utils/makeParams';
import { serializedGatewayFetch } from '@utils/sessionFetch';
import { ISerializedResponse } from '@utils/types/SerializedResponse';

const SCHEDULED_POST_DEFAULT_ENDPOINT = '/api/v1/scheduled-posts';

const universalScheduledPostFetch = (
  endpoint: string,
  init?: RequestInit,
): Promise<ISerializedResponse> => {
  return serializedGatewayFetch(
    'universal_scheduling_gateway',
    `${SCHEDULED_POST_DEFAULT_ENDPOINT}${endpoint}`,
    init,
  );
};

/**
 * Gets scheduled posts for the current client
 *
 * GET /api/v1/scheduled-posts
 */
export const searchScheduledPosts = async (
  req: ISearchScheduledPostsRequest,
): Promise<IGetScheduledPostsResponse> => {
  const params = makeParams(req);
  const res = await universalScheduledPostFetch(`?${params}`);

  const scheduledPostsResponse = await res.json<IGetScheduledPostsResponse>(
    (data) => ({
      data: data.data.map(deserializeScheduledPost),
      totalCount: data.totalCount,
    }),
  );

  return scheduledPostsResponse;
};

/**
 * Gets a scheduled post by id for the current client
 *
 * GET /api/v1/scheduled-posts/:id
 */
export const getScheduledPost = async (
  id: PostableId,
): Promise<IScheduledPost> => {
  const res = await universalScheduledPostFetch(`/${id}`);

  const postResponse = await res.json<IScheduledPostResponse>((data) => ({
    data: deserializeScheduledPost(data.data),
  }));

  return postResponse.data;
};

/**
 * Gets a batch of scheduled posts by id for the current client
 *
 * GET /api/v1/scheduled-posts/batch
 */
export const getScheduledPosts = async (
  ids: PostableId[],
): Promise<IScheduledPost[]> => {
  if (ids.length === 0) {
    return [];
  }

  const params = makeParams({ ids });

  const res = await universalScheduledPostFetch(`/batch?${params}`);

  const postResponse = await res.json<IGetBatchScheduledPostsResponse>(
    (data) => ({
      data: {
        scheduledPosts: data.data.scheduledPosts.map(deserializeScheduledPost),
      },
    }),
  );

  return postResponse.data.scheduledPosts;
};

/**
 * Creates scheduled posts
 *
 * POST /api/v1/scheduled-posts/batch
 */
export const createScheduledPosts = async (
  posts: ICreateUpdateScheduledPost[],
): Promise<PostableId[]> => {
  const res = await universalScheduledPostFetch(`/batch`, {
    method: 'POST',
    body: JSON.stringify({
      posts,
    }),
  });

  const createdPostsResponse = await res.json<BatchCreatePostsResponse>();

  return createdPostsResponse.data;
};

/**
 * Upserts scheduled posts
 *
 * POST /api/v1/scheduled-posts/batch/upsert
 */
export const upsertScheduledPosts = async (
  scheduledPosts: ICreateUpdateScheduledPost[],
  connectionId?: ConnectionId,
  connectionConfiguration?: TConnectionConfig,
): Promise<PostableId[]> => {
  const res = await universalScheduledPostFetch(`/batch/upsert`, {
    method: 'POST',
    body: JSON.stringify({
      scheduledPosts,
      connectionId,
      connectionConfiguration,
    }),
  });

  const postableIdsResponse =
    await res.json<BatchUpsertScheduledPostsResponse>();

  return postableIdsResponse.data;
};

/**
 * Updates a scheduled post
 *
 * PUT /api/v1/scheduled-posts/:id
 */
export const updateScheduledPost = async (
  id: PostableId,
  updatedPost: ICreateUpdateScheduledPost,
): Promise<IScheduledPost> => {
  const res = await universalScheduledPostFetch(`/${id}`, {
    method: 'PUT',
    body: JSON.stringify({
      updatedPost,
    }),
  });

  const postResponse = await res.json<IScheduledPostResponse>((data) => ({
    data: deserializeScheduledPost(data.data),
  }));

  return postResponse.data;
};

/**
 * Marks a post initiated
 *
 * GET /api/v1/scheduled-posts/mark-initiated/:id
 */
export const markPostInitiated = async (id: PostableId): Promise<void> => {
  await universalScheduledPostFetch(`/mark-initiated/${id}`, {
    method: 'POST',
  });
};

/**
 * Marks a post posted
 *
 * GET /api/v1/scheduled-posts/mark-posted/:id
 */
export const markPostPosted = async (id: PostableId): Promise<void> => {
  await universalScheduledPostFetch(`/mark-posted/${id}`, {
    method: 'POST',
  });
};

/**
 * Reschedule scheduled posts
 *
 * PATCH /api/v1/scheduled-posts/batch/reschedule
 */
export const reschedulePosts = async (
  updates: ScheduledPostTimeUpdate[],
): Promise<PostableId[]> => {
  const res = await universalScheduledPostFetch(`/batch/reschedule`, {
    method: 'PATCH',
    body: JSON.stringify({
      updates,
    }),
  });

  const createdPostsResponse = await res.json<BatchCreatePostsResponse>();

  return createdPostsResponse.data;
};

/**
 * Deletes scheduled posts by id
 *
 * DELETE /api/v1/scheduled-posts/batch/
 */
export const deleteScheduledPosts = async (
  ids: PostableId[],
): Promise<void> => {
  const params = makeParams({ ids });

  await universalScheduledPostFetch(`/batch?${params}`, {
    method: 'DELETE',
  });
};
